import { Dayjs } from "dayjs"
import { t } from "i18next"
import * as Yup from "yup"
export type PlanType = "draft" | "non-draft"
export interface PlanFormFields {
  user_id: string | number
  recipient_number: string | null
  facility_id: string | null
  facility_admin: string | null
  use_service_id: string | null
  create_count: number | string
  agree_date: Dayjs | null
  start_date: Dayjs | null
  end_date: Dayjs | null
  staff_id: string | null
  support_pran_draft_type?: boolean
  user_family_hope?: string | null
  support_policy?: string | null
  long_term_goal?: string | null
  short_term_goal?: string | null
  goto_meet_flag?: string | null
  goto_meet_place?: string | null
  take_flag?: string | null
  take_place?: string | null
  remarks?: string | null
  isDraft: false
  furigana?: string | null
}
export interface ActualPlanSearchParams {
  keyword?: string
  user_id?: number
  service?: any
  facilityIds?: string[] | []
  date?: any
  expiry_date?: any
  display_settings?: string
  status?: string[] | []
  page?: number
  pageSize?: number
  user_without_services?: string
}
export interface PlanSearchParams extends ActualPlanSearchParams {
  date_from?: string
  date_to?: string
  expiry_date_from?: string
  expiry_date_to?: string
  hiragana?: string
}
interface staff {
  id: number
  staff_name: string
}
interface IndividualPlanCommon {
  id: number
  key?: number
  created_datetime: string
  created_by?: string
  updated_datetime?: string
  updated_by?: string
  deleted_datetime?: string | null
  deleted_by?: string
  is_deleted?: boolean
  company_id: number
  user_id: number
  facility_id: number
  create_count: number
  report_create_date: number | null
  start_date: string | null
  end_date: string | null
  staff_id: string | null
  staff: staff | null
  support_plan_draft_type: string | null
  child_family_hope: string | null
  support_policy: string | null
  long_term_goal: string | null
  short_term_goal: string | null
  goto_meet_flag: string | null
  goto_meet_place: string | null
  take_flag: string | null
  take_place: string | null
  remarks: string | null
  agree_date: string | null
  draft_save_flag: string | null
  use_service_id: number | null
  child_info?: any
  facility?: any
  digital_signature_image?: string
  child_digital_signature_image?: string
  manager_digital_signature_image?: string
  explanation_date_digital_signature_image?: string
  consent_date_digital_signature_image?: string
}

// Detect dangerous text patterns that can break JSON/DB parsing or rendering
export const detectDangerText = (text: string): string[] => {
  const issues: string[] = []
  const tnorm = (text ?? "").replace(/\r\n?/g, "\n")
  // JSON-like structure
  if (/(?:\{|\[)\s*"[A-Za-z0-9_-]+"\s*:\s*/.test(tnorm)) {
    issues.push("JSON形式の文字列")
  }
  // Line ending with a single backslash
  if (tnorm.split("\n").some((line) => /(^|[^\\])\\$/.test(line))) {
    issues.push("行末バックスラッシュ")
  }
  // Unclosed quotes
  const dq = (tnorm.match(/"/g) || []).length
  if (dq % 2 === 1) {
    issues.push("未閉じのダブルクォート")
  }
  // Control characters other than TAB/LF/CR
  for (let i = 0; i < tnorm.length; i++) {
    const code = tnorm.charCodeAt(i)
    if (code < 32 && code !== 9 && code !== 10 && code !== 13) {
      issues.push("制御文字")
      break
    }
  }
  return issues
}

// Yup helper: add danger text validation with a localized label
const dangerText = () =>
  Yup.string()
    .nullable()
    .test("danger-text", function (value) {
      const issues = detectDangerText(value || "")
      if (issues.length === 0) return true
      const pretty = Array.from(new Set(issues))
        .map((v) => {
          if (v === "未閉じのダブルクォート") return '"（ダブルクォート）'
          if (v === "行末バックスラッシュ") return '行末の "\\"'
          if (v === "制御文字") return "制御文字"
          // JSON形式の表現はユーザー向けに出さない
          return null
        })
        .filter(Boolean) as string[]
      // JSON だけが検出された場合は、ユーザー向けに分かりやすい代替表現を出す
      if (pretty.length === 0 && issues.includes("JSON形式の文字列")) {
        pretty.push('記号の組合せ（" { } [ ] :）')
      }
      return this.createError({
        message: `入力に使用できない文字／形式があります。修正してください。該当: ${pretty.join(
          "、",
        )}`,
      })
    })

export const PlanFormSchema = Yup.object({
  user_id: Yup.number().required(t("Required")).nullable(),
  facility_id: Yup.number().min(1, t("Required")).required(t("Required")),
  use_service_id: Yup.number().required(t("Required")),
  create_count: Yup.number().required(t("Required")),
  goto_meet_place: Yup.string().nullable().max(100, t("(Within 100 char)")),
  take_place: Yup.string().nullable().max(100, t("(Within 100 char)")),
  // Added field-level dangerous text validations
  user_family_hope: dangerText(),
  support_policy: dangerText(),
  long_term_goal: dangerText(),
  short_term_goal: dangerText(),
  remarks: dangerText(),
})

export interface UsersWhoNeedMonitoringParams {
  user_id?: number
  service?: string
  facility?: string
  date?: string
  expiry_date?: string
  display_settings?: string
  status?: string
  page?: number
  pageSize?: number | string
  user_without_services?: string
}

interface staff {
  id: number
  staff_name: string
}

export interface IndividualSupportPlanItemObj {
  id: number
  created_datetime: string | null
  created_by: string | null
  updated_datetime: string | null
  updated_by: string | null
  deleted_datetime: string | null
  deleted_by: string | null
  is_deleted: boolean
  company_id: number | null
  child_support_pran_draft_id: number | null
  title: string
  support_content?: {
    achievement_goals: string
    achievement_time: string
    support_and_considerations: string
  }
}
export interface IndividualSupportWeeklyScheduleObj {
  user_support_plan_id?: number
  comment?: string
  company_id: number
  created_by: string
  created_datetime: string
  deleted_by: string
  deleted_datetime?: string | null
  end_time: string | null
  id: number
  is_deleted?: boolean | null
  start_time: string
  updated_by: string
  updated_datetime: string
  week_no: number
}
export interface IndividualSupportPlanResponse extends IndividualPlanCommon {
  plan_items: IndividualSupportPlanItemObj[]
  weekly_schedules: IndividualSupportWeeklyScheduleObj[]
}

export interface IndividualSupportDetailResponse {
  childSupport: IndividualPlanCommon
  childSupportItems: IndividualSupportPlanItemObj[]
  childSupportSchedule: IndividualSupportWeeklyScheduleObj[]
}

export interface PlanSignatureResponseObj {
  digital_signature_image?: string //parent signature
  child_digital_signature_image?: string
  manager_digital_signature_image?: string
  manager_date_digital_signature_image?: string
  explanation_date_digital_signature_image?: string
  consent_date_digital_signature_image?: string
  user_id: number
  planId: number
  facility_id: number
}
export interface PlanOperationData {
  display_content: any[]
  consent_date: any[]
  parent_sign: any[]
  statementText: {
    consentStatement: string
    consentStatementForChild: string
  }
  print_settings: string
}

export interface IndividualSupportPlanSignatures {
  digital_signature_image?: string
  user_digital_signature_image?: string
  manager_digital_signature_image?: string
  explanation_date_digital_signature_image?: string
  consent_date_digital_signature_image?: string
}
